import { AEDModel } from '../../models/AEDModel'
import { AgencyModel } from '../../models/AgencyModel'
import { AgencySearchItemModel } from '../../models/AgencySearchItemModel'
import { Environment } from '../../models/Environment'
import { IncidentFeedModel } from '../../models/IncidentFeedModel'
import { IncidentModel } from '../../models/IncidentModel'
import { IncidentPostData } from '../../models/IncidentPostData'
import { arrayFromJSONValue } from '../../models/parser'
import { MapLayerModel } from '../../models/MapLayerModel'
import { UnitLegendModel } from '../../models/UnitLegend'
import { client } from '../../utils/client'
import { decryptResponse } from './DecryptResponse'
import { CognitoUserSession } from 'amazon-cognito-identity-js'

export const fetchAgencySearchResultsWithToken = (
  token: string,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  if (token.length >= 2) {
    return client(
      `/${version}/webapp?resource=searchagencies&token=${encodeURIComponent(token)}`,
      undefined,
      'GET',
      session
    )
      .then(encryptedData => decryptResponse(encryptedData))
      .then(data => arrayFromJSONValue(data['searchagencies']).map(value => AgencySearchItemModel.modelFromJSON(value)))
  } else {
    return Promise.resolve([])
  }
}

export const fetchAgencyShape = (
  shapeId: string,
  token: string,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const shapeParam = `&ogr_fid=${shapeId}`
  const tokenParam = token.length > 0 ? `&token=${encodeURIComponent(token)}` : ''
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(`/${version}/webapp?resource=agencyshape${shapeParam}${tokenParam}`, undefined, 'GET', session)
    .then(encryptedData => decryptResponse(encryptedData))
    .then(data => {
      return data['WKT']
    })
}

export const fetchAgencyUnitLegend = (
  agencyId: string,
  token: string,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const agencyParam = `&agencyid=${agencyId}`
  const tokenParam = token.length > 0 ? `&token=${encodeURIComponent(token)}` : ''
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(`/${version}/webapp?resource=unitlegend${agencyParam}${tokenParam}`, undefined, 'GET', session)
    .then(encryptedData => decryptResponse(encryptedData))
    .then(data => {
      return arrayFromJSONValue(data['UnitLegend']).map(value => UnitLegendModel.modelFromJSON(value))
    })
}

export const fetchAgencies = (
  agencyIDs: string[],
  token: string,
  psap: boolean,
  environment: Environment,
  session?: CognitoUserSession
) => {
  if (agencyIDs.length === 0 && token.length === 0) {
    return Promise.reject('No agencies')
  }

  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  const psapParam = psap ? '&psap=1' : ''
  const agenciesParam = agencyIDs.length > 0 ? `&agencyid=${agencyIDs.join(',')}` : ''
  const tokenParam = token.length > 0 ? `&token=${encodeURIComponent(token)}` : ''
  return client(
    `/${version}/webapp?resource=agencies${agenciesParam}${tokenParam}${psapParam}`,
    undefined,
    'GET',
    session
  )
    .then(encryptedData => decryptResponse(encryptedData))
    .then(data => {
      return arrayFromJSONValue(data['agencies']).map(value => AgencyModel.modelFromJSON(value))
    })
}

export const fetchIncidents = (
  agencyIDs: string[],
  token: string,
  psap: boolean,
  environment: Environment,
  session?: CognitoUserSession
) => {
  if (agencyIDs.length === 0 && token.length === 0) {
    return Promise.resolve(IncidentFeedModel.modelFromJSON({}))
  }

  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  const psapParam = psap ? '&psap=1' : ''
  const agenciesParam = agencyIDs.length > 0 ? `&agencyid=${agencyIDs.join(',')}` : ''
  const tokenParam = token.length > 0 ? `&token=${encodeURIComponent(token)}` : ''
  return client(
    `/${version}/webapp?resource=incidents${agenciesParam}${tokenParam}${psapParam}`,
    undefined,
    'GET',
    session
  )
    .then(encryptedData => decryptResponse(encryptedData))
    .then(value => {
      return IncidentFeedModel.modelFromJSON(value['incidents'])
    })
}

export const fetchIncident = (incidentID: string, environment: Environment, session?: CognitoUserSession) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(`/${version}/webapp?resource=incident&incidentid=${incidentID}`, undefined, 'GET', session)
    .then(encryptedData => decryptResponse(encryptedData))
    .then(data => IncidentModel.modelFromJSON(data))
}

export const fetchAEDs = (viewport: string, environment: Environment, session?: CognitoUserSession) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(`/${version}/webapp?resource=aeds&viewport=${viewport}`, undefined, 'GET', session)
    .then(encryptedData => {
      return decryptResponse(encryptedData)
    })
    .then(data => {
      return arrayFromJSONValue(data['aeds']).map(value => AEDModel.modelFromJSON(value))
    })
}

export const fetchMapLayers = (viewport: string, layers: string, environment: Environment, session?: CognitoUserSession) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(`/${version}/webapp?resource=maplayers&viewport=${viewport}&layers=${layers}`, undefined, 'GET', session)
    .then(encryptedData => {
      return decryptResponse(encryptedData)
    })
    .then(data => {
      return Object.fromEntries(
        Object.entries(data['Layers']).map(([key, value]) => 
          [key, arrayFromJSONValue(value).map(v => MapLayerModel.modelFromJSON(v))]
        )
      )
    })
}

export const fetchIncidentJSON = (
  incidentID: string,
  token: string,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  const tokenParam = token !== '' ? `&token=${encodeURIComponent(token)}` : ''
  return client(`/${version}/webapp?resource=json&incidentid=${incidentID}${tokenParam}`, undefined, 'GET', session)
    .then(encryptedData => decryptResponse(encryptedData))
    .then(data => {
      const value = JSON.stringify(data, null, 2)
      return value
    })
}

export const submitIncidentPost = (
  incidentPost: IncidentPostData,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  let data = { ...incidentPost, action: 'update-post' }
  return client(`/${version}/incident_post_admin`, data, 'POST', session)
}

export const deleteIncidentPost = (
  postID: string,
  incidentID: string,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(`/${version}/incident_post_admin?incidentid=${incidentID}&id=${postID}`, undefined, 'DELETE', session)
}

export const closeIncident = (
  incidentNumber: string,
  agencyID: string,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(
    `/${version}/incident_override`,
    {
      IncidentNumber: incidentNumber,
      AgencyID: agencyID,
      Close: true
    },
    'POST',
    session
  )
}

export const reopenIncident = (
  incidentNumber: string,
  agencyID: string,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(
    `/${version}/incident_override`,
    {
      IncidentNumber: incidentNumber,
      AgencyID: agencyID,
      Close: false
    },
    'POST',
    session
  )
}

export const suppressFromPublic = (
  incidentNumber: string,
  agencyID: string,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(
    `/${version}/incident_override`,
    {
      IncidentNumber: incidentNumber,
      AgencyID: agencyID,
      SuppressFrom: 'PUBLIC'
    },
    'POST',
    session
  )
}

export const suppressFromAll = (
  incidentNumber: string,
  agencyID: string,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(
    `/${version}/incident_override`,
    {
      IncidentNumber: incidentNumber,
      AgencyID: agencyID,
      SuppressFrom: 'ALL'
    },
    'POST',
    session
  )
}

export const markAddressPrivate = (
  incidentNumber: string,
  agencyID: string,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(
    `/${version}/incident_override`,
    {
      IncidentNumber: incidentNumber,
      AgencyID: agencyID,
      Location: 'Private'
    },
    'POST',
    session
  )
}

export const markAddressPublic = (
  incidentNumber: string,
  agencyID: string,
  environment: Environment,
  session?: CognitoUserSession
) => {
  const version = environment === 'SANDBOX' ? 'v1-sandbox' : 'v1'
  return client(
    `/${version}/incident_override`,
    {
      IncidentNumber: incidentNumber,
      AgencyID: agencyID,
      Location: 'Public'
    },
    'POST',
    session
  )
}
